import { useState, useEffect } from 'react';

interface PromoPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PromoPopup({ isOpen, onClose }: PromoPopupProps) {
  const [formData, setFormData] = useState({ nom: '', telephone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => setAnimateIn(true), 20);
    } else {
      document.body.style.overflow = 'unset';
      setAnimateIn(false);
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');
    try {
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => submitData.append(key, value));
      submitData.append('typeOffre', 'Pompe à chaleur à 0€ - CEE');
      const response = await fetch('https://readdy.ai/api/form/d2rc6242132hm7p4bqm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: submitData.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nom: '', telephone: '', email: '' });
        setTimeout(() => { onClose(); setSubmitStatus(''); }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-slate-900/75 backdrop-blur-sm transition-opacity duration-300 ${animateIn ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-lg bg-white rounded-3xl overflow-hidden transition-all duration-300 ${animateIn ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}
        style={{ maxHeight: '92vh', overflowY: 'auto' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg text-white"></i>
        </button>

        {/* Hero Banner */}
        <div className="relative bg-slate-900 px-8 pt-10 pb-8 overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-orange-400"></div>
            <div className="absolute -bottom-12 -left-8 w-56 h-56 rounded-full bg-orange-500"></div>
          </div>

          {/* Badge */}
          <div className="relative flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 bg-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
              <i className="ri-award-line"></i>
              Offre CEE exclusive
            </span>
          </div>

          <div className="relative text-center">
            <p className="text-slate-400 text-sm font-medium mb-1 uppercase tracking-widest">Pompe à chaleur</p>
            <h2 className="text-5xl font-black text-white leading-none mb-2">
              À <span className="text-orange-400">0 €</span>
            </h2>
            <p className="text-slate-300 text-base">
              Grâce aux Certificats d'Économies d'Énergie
            </p>
          </div>

          {/* Stats strip */}
          <div className="relative mt-7 grid grid-cols-3 gap-3 text-center">
            {[
              { val: '70%', label: "d'économies" },
              { val: '0 €', label: 'reste à charge' },
              { val: '48h', label: 'devis gratuit' },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 rounded-xl py-3 px-2">
                <div className="text-orange-400 font-black text-xl leading-tight">{s.val}</div>
                <div className="text-slate-400 text-xs mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="px-8 py-6 border-b border-slate-100">
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: 'ri-leaf-line', color: 'text-emerald-500 bg-emerald-50', label: 'PAC air/air & air/eau' },
              { icon: 'ri-money-euro-circle-line', color: 'text-orange-500 bg-orange-50', label: 'Financement Sofinco' },
              { icon: 'ri-shield-star-line', color: 'text-slate-600 bg-slate-100', label: 'Certifié RGE' },
              { icon: 'ri-tools-line', color: 'text-teal-500 bg-teal-50', label: 'Installation pro incluse' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${b.color}`}>
                  <i className={`${b.icon} text-base`}></i>
                </div>
                <span className="text-sm font-medium text-slate-700">{b.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="px-8 py-7">
          <h3 className="text-lg font-bold text-slate-900 mb-1">Vérifiez votre éligibilité</h3>
          <p className="text-slate-500 text-sm mb-5">Un expert vous rappelle sous 24h — sans engagement.</p>

          <form id="promo-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Nom complet *
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                placeholder="Jean Dupont"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all placeholder-slate-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Téléphone *
              </label>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                required
                placeholder="06 00 00 00 00"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all placeholder-slate-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all placeholder-slate-400"
              />
            </div>

            {submitStatus === 'success' && (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-xl flex-shrink-0"></i>
                <p className="text-emerald-800 text-sm font-medium">
                  Super ! On vous recontacte très vite pour confirmer votre éligibilité.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <i className="ri-error-warning-fill text-red-500 text-xl flex-shrink-0"></i>
                <p className="text-red-800 text-sm font-medium">Une erreur s'est produite. Veuillez réessayer.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  Vérification en cours...
                </>
              ) : (
                <>
                  <i className="ri-arrow-right-circle-fill text-base"></i>
                  Je vérifie mon éligibilité gratuitement
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-slate-400 text-center mt-4 leading-relaxed">
            Offre soumise aux conditions d'éligibilité CEE — Dispositif gouvernemental
          </p>
        </div>
      </div>
    </div>
  );
}